import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MerchantService } from '@core/merchant/merchant.service';
import { Merchant } from '@core/merchant/merchant.types';
import { Subject, take, takeUntil } from 'rxjs';
import { Location } from '@angular/common';
import { MatButton, MatAnchor } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { TranslocoDirective, TranslocoModule } from '@jsverse/transloco';

/**
 * Component that represents the 404 Not Found page.
 */
@Component({
    selector: 'notFound',
    templateUrl: './notFound.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        TranslocoModule,
        TranslocoDirective,
        MatIcon,
        RouterModule,
        MatButton,
        MatAnchor
    ]
})
export class NotFoundComponent implements OnDestroy {

    /**
     * The merchant object.
     */
    public merchant: Merchant | null = null;

    /**
     * The path of the current route.
     */
    public path: string;

    /**
     * Subject that emits when the component is destroyed.
     */
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     *
     * @param _merchantService The merchant service.
     * @param _changeDetectorRef The change detector reference.
     * @param _route The activated route.
     * @param location The location service.
     */
    constructor(
        private _merchantService: MerchantService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _route: ActivatedRoute,
        private location: Location
    ) {
        // Get the merchant
        this._merchantService.merchant$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((merchant: Merchant | null) => {
                this.merchant = merchant;
                // Notify the change detector
                this._changeDetectorRef.markForCheck();
            });
        this._route.data.pipe(take(1))
            .subscribe((data: any) => {
                if (data && 'path' in data) {
                    this.path = data['path'];
                }
            });
    }

    /**
     * Lifecycle hook that is called when the component is destroyed.
     */
    ngOnDestroy() {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    /**
     * Navigates back to the previous location in the browser's history.
     */
    goBack(): void {
        this.location.back();
    }
}
